// UAVSimNet brand mark.
// - Inline SVG glyph (no rasterisation): a clean 5-point star outline drawn as
//   a single 10-vertex polygon (single uniform stroke, no fill) plus a tiny
//   apex dot. Uses currentColor so callers can recolour via Tailwind text-*.
// - Wordmark renders as flowing HTML text (system font, not SVG) so the OS
//   handles glyph hinting cleanly.

const SIZE_MAP = {
    sm: { glyph: 20, text: 12, gap: 8 },
    md: { glyph: 28, text: 16, gap: 10 },
    lg: { glyph: 40, text: 22, gap: 12 },
    xl: { glyph: 64, text: 32, gap: 16 },
};

// Pre-computed vertices of a 5-point star inside a 64x64 viewBox.
// 10 points alternating outer R=24 and inner r=10 around centre (32, 33),
// starting at the top apex (32, 9). Stroked with width 2; no fill.
const STAR_POINTS =
    "32,9 37.9,24.9 54.8,25.6 41.5,36.1 46.1,52.4 32,43 17.9,52.4 22.5,36.1 9.2,25.6 26.1,24.9";

export function LogoGlyph({ size = 28, className = "" }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 64 64"
            width={size}
            height={size}
            role="img"
            aria-label="UAVSimNet"
            className={className}
            data-testid="logo-glyph-svg"
        >
            <polygon
                points={STAR_POINTS}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
                strokeLinecap="round"
            />
            {/* Apex dot — tiny circle just above the top point of the star. */}
            <circle cx="32" cy="6" r="1.6" fill="currentColor" />
        </svg>
    );
}

export default function Logo({
    variant = "wordmark",
    size = "md",
    className = "",
}) {
    const dims = SIZE_MAP[size] ?? SIZE_MAP.md;

    if (variant === "glyph") {
        return (
            <span
                className={`inline-flex text-[var(--ux-accent)] ${className}`}
                data-testid="logo-glyph"
            >
                <LogoGlyph size={dims.glyph} />
            </span>
        );
    }

    if (variant === "stacked") {
        return (
            <span
                className={`inline-flex flex-col items-center ${className}`}
                style={{ gap: dims.gap / 2 }}
                data-testid="logo-stacked"
            >
                <span className="text-[var(--ux-accent)]">
                    <LogoGlyph size={dims.glyph} />
                </span>
                <span
                    className="font-extrabold uppercase text-[var(--ux-text-1)]"
                    style={{ fontSize: dims.text, letterSpacing: "0.18em" }}
                >
                    UAVSIMNET
                </span>
            </span>
        );
    }

    // wordmark (default)
    return (
        <span
            className={`inline-flex items-center ${className}`}
            style={{ gap: dims.gap }}
            data-testid="logo-wordmark"
        >
            <span className="text-[var(--ux-accent)] inline-flex">
                <LogoGlyph size={dims.glyph} />
            </span>
            <span
                className="font-extrabold uppercase text-[var(--ux-text-1)]"
                style={{ fontSize: dims.text, letterSpacing: "0.18em" }}
            >
                UAVSIMNET
            </span>
        </span>
    );
}
